import React from "react";
import { getPaginationText } from './getPaginationText';
import './DataTablePagination.scss';
import { DataTableState, DataTableSorting, DataTableFilter } from "./DataTable";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { relative } from "path";
import { handleRenderProp, RenderProp } from "../../utils/handleRenderProp";

const DefaultButton = (props: any) => <button type="button" {...props}>{props.children}</button>;

type State = {
  visiblePages: any[]
}

export interface DataTablePaginationProps {
  //these come from react-table
  pages: number;
  page: number;
  sorted: DataTableSorting[];
  filtered: DataTableFilter[];
  PageButtonComponent: any;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: any) => void;
  onFetchData?: (state: DataTableState) => void;
  previousText: string;
  nextText: string;
  isTop?: boolean;
  pageSize?: number;
  pageSizeOptions: number[];
  getPaginationProps?: () => any;
  data: any[];
  id?: string;

  //this is ours
  setFilter: (globalFilter: string) => void;
  filter?: string;
  debouncing: boolean;
  setDebouncing: (val: boolean) => void;
  useDataFetching: boolean;
  totalRows?: number;
  gridActions?: JSX.Element | RenderProp | string;
}

function filterPages(visiblePages: number[], totalPages: number) {
  return visiblePages.filter(page => page <= totalPages);
};

function getVisiblePages(page: number, total: number) {
  if (total < 7) {
    return filterPages([1, 2, 3, 4, 5, 6], total);
  } else {
    if (page % 5 >= 0 && page > 4 && page + 2 < total) {
      return [1, page - 1, page, page + 1, total];
    } else if (page % 5 >= 0 && page > 4 && page + 2 >= total) {
      return [1, total - 3, total - 2, total - 1, total];
    } else {
      return [1, 2, 3, 4, 5, total];
    }
  }
};

export interface SearchInputProps {
  onChange: any;
  debouncing: boolean;
}
const SearchInput: React.FC<SearchInputProps> = (p) => {
  return (
    <div className="search-box">
      <label>
        Search:
        <input
          key="dt-filter"
          type="text"
          name="dt-filter"
          className="form-control input-sm dt-filter"
          data-testid="dt-filter"
          onChange={p.onChange}
        />
        <span style={{
          position: 'absolute',
          float: 'right',
          marginTop: '6px'
        }}>
        <LoadingIndicator show={p.debouncing} size={10} />
        </span>
      </label>
    </div>
  );
}

export class DataTablePagination extends React.Component<DataTablePaginationProps, State> {
  constructor(props: DataTablePaginationProps) {
    super(props);

    this.state = {
      visiblePages: getVisiblePages(1, props.pages)
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: DataTablePaginationProps) {
    if (this.props.pages !== nextProps.pages) {
      this.setState({
        visiblePages: getVisiblePages(1, nextProps.pages)
      });
    }

    this.changePage(nextProps.page + 1);
  }

  changePage = (page: number) => {
    const activePage = this.props.page + 1;

    if (page === activePage) {
      return;
    }

    const visiblePages = getVisiblePages(page, this.props.pages);

    this.setState({
      visiblePages: filterPages(visiblePages, this.props.pages)
    });

    this.props.onPageChange(page - 1);
  }

  handleSetFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.setFilter(e.target.value);
    if (!!this.props.useDataFetching) {
      this.props.setDebouncing(true);
    }
  }

  render() {
    const PageButtonComponent = this.props.PageButtonComponent || DefaultButton;
    const { visiblePages } = this.state;
    const activePage = this.props.page + 1;

    const { isTop, pageSize, page, totalRows, data } = this.props;

    const pos = isTop ? "top" : "bottom";

    const _pageSize = pageSize || 10;

    return (
      <div className="Table__pager">
        <div className="Table__pageControls">
          {!isTop &&
            <div className="Table__itemCount">
              <span data-testid="dt-entries">{getPaginationText(page, _pageSize || 10, totalRows || data.length)}</span>
              <span className="page-size-select">
                <strong>Show</strong>
                <select name="pageSize"
                  className="form-inline input-sm"
                  value={this.props.pageSize}
                  data-testid={`dt-pagesize-${pos}`}
                  onChange={(e) => this.props.onPageSizeChange(e.target.value)}>
                  {this.props.pageSizeOptions.map((ps: number) => <option value={ps} key={ps} data-testid="dt-pagesize-option">{ps}</option>)}
                </select>
                <strong>Entries</strong>
              </span>
            </div>
          }
          {isTop && <SearchInput onChange={this.handleSetFilter} debouncing={this.props.debouncing} />}
          {isTop && this.props.gridActions && handleRenderProp(this.props.gridActions)}
        </div>
        <div className="Table__pagination">
          <div className="Table__prevPageWrapper">
            <PageButtonComponent
              className="Table__pageButton"
              data-testid={`dt-pageindex-prev-${pos}`}
              onClick={() => {
                if (activePage === 1) return;
                this.changePage(activePage - 1);
              }}
              disabled={activePage === 1}
            >
              Previous
            </PageButtonComponent>
          </div>
          <div className="Table__visiblePagesWrapper">
            {visiblePages.map((page, index, array) => {
              return (
                <PageButtonComponent
                  key={`${page}-${index}`}
                  className={
                    activePage === page
                      ? "Table__pageButton Table__pageButton--active"
                      : "Table__pageButton"
                  }
                  data-testid="dt-pageindex"
                  onClick={this.changePage.bind(null, page)}
                >
                  {array[index - 1] + 2 < page ? `...${page}` : page}
                </PageButtonComponent>
              );
            })}
          </div>
          <div className="Table__nextPageWrapper">
            <PageButtonComponent
              className="Table__pageButton"
              data-testid={`dt-pageindex-next-${pos}`}
              onClick={() => {
                if (activePage === this.props.pages) return;
                this.changePage(activePage + 1);
              }}
              disabled={activePage === this.props.pages}
            >
              {this.props.nextText}
            </PageButtonComponent>
          </div>
        </div>
      </div>
    );
  }
}

(DataTablePagination as any).displayName = 'DataTablePagination';